import {ActionIcon, Paper, SegmentedControl} from "@mantine/core";
import {Checkbox} from "@mantine/core";
import {Modal} from "@mantine/core";
import {NumberInput} from "@mantine/core";
import {Button} from "@mantine/core";
import { InfoIcon, TrashIcon } from "@phosphor-icons/react";
import React, {useState, useMemo, useCallback, useEffect, useRef} from 'react';
import withRouteErrorBoundary from "../../components/ErrorBoundary/withRouteErrorBoundary.tsx";
import {Col, Row} from "../../components/Flex/Flex";
import Page from "../../components/Page/Page.tsx";
import AFImporter from "./AFImporter";
import style from './VisionTool.module.css';
import {v4 as uuidv4} from "uuid";
import {useParams} from "react-router";
import useSavedItem from "../../components/SharedSave/useSavedItem.ts";
import SharedSave from "../../components/SharedSave/SharedSave.tsx";
import {track} from "../../utils/analytics.ts";
import {CellResult, FieldMap, gridMax, OBSTACLE, simulate, Source} from "./simulation.ts";
import type {OptimizeProgress, OptimizeRequest, OptimizeResult} from "./optimizer/optimize.ts";
import type {OptimizerMessage} from "./optimizer/optimizer.worker.ts";


const modes: {label: string, value: TileMode}[] = [
    {label: 'None', value: 'read'},
    {label: 'Source', value: 'source'},
    {label: 'Obstacle', value: 'blocking'},
    {label: 'Asteroid', value: 'asteroid'},
]
const locations = [
    {label: 'Space', value: 'space'},
    {label: 'Ground', value: 'ground'}
]

type TileMode = 'read'|'asteroid'|'blocking'|'source';

type OptimizeOutcome = (OptimizeResult | {status: 'error', message: string}) & {
    // the map the result refers to, it is only shown while the map is unchanged
    basis: {field: FieldMap, sources: Source[], location: string},
};

let initial: FieldMap = [];
for (let y = 0; y <= 21; y++) {
    initial[y] = [];
    for (let x = 0; x <= 21; x++) {
        initial[y][x] = OBSTACLE.NONE;
    }
}

function VisionTool() {
    const {id} = useParams();
    const savedData = useSavedItem('vision', id);
    const [field, setField] = useState<FieldMap>(initial);
    const [sources, setSources] = useState<Source[]>([]);
    const [mode, setMode] = useState<TileMode>('read');
    const [location, setLocation] = useState('space');
    const [showInfo, setShowInfo] = useState(false);
    const [optimizeSensors, setOptimizeSensors] = useState(10);
    const [optimizing, setOptimizing] = useState(false);
    const [progress, setProgress] = useState<OptimizeProgress | null>(null);
    const [outcome, setOutcome] = useState<OptimizeOutcome | null>(null);
    const workerRef = useRef<Worker | null>(null);

    const savedContent = savedData?.content;

    useEffect(() => {
        if (!savedContent) {
            return;
        }
        setField(savedContent.field ?? initial);
        setSources(savedContent.sources ?? []);
        setLocation(savedContent.location ?? 'space');
    }, [savedData?.id, savedContent]);

    const currentData = useMemo(() => ({
        field,
        sources,
        location
    }), [field, sources, location]);

    const draw = useMemo(() => simulate(field, sources, location), [field, sources, location]);

    const [countBlocking, countAsteroids] = useMemo(() => {
        return [
            field.reduce((tot, row) => tot + row.reduce((acc, cell) => acc + (cell === OBSTACLE.BLOCKING ? 1 : 0), 0), 0),
            field.reduce((tot, row) => tot + row.reduce((acc, cell) => acc + (cell === OBSTACLE.ASTEROID ? 1 : 0), 0), 0),
        ]
    }, [field]);
    const coverage = useMemo(() => {
        return draw.reduce((tot, row) => tot + row.reduce((acc, cell) => acc + (cell.sensors ? 1 : 0), 0), 0);
    }, [draw]);
    const totalTiles = draw.length * draw.length;


    const stopWorker = useCallback(() => {
        workerRef.current?.terminate();
        workerRef.current = null;
        setOptimizing(false);
        setProgress(null);
    }, []);

    // a running optimization is outdated as soon as the map changes
    useEffect(() => () => {
        if (workerRef.current) {
            stopWorker();
        }
    }, [field, sources, location, stopWorker]);

    const optimizeSources = useCallback(() => {
        stopWorker();
        const sensors = optimizeSensors;
        const started = performance.now();
        const worker = new Worker(new URL('./optimizer/optimizer.worker.ts', import.meta.url), {type: 'module'});
        workerRef.current = worker;
        setOptimizing(true);
        setOutcome(null);

        const fail = (message: string) => {
            stopWorker();
            setOutcome({status: 'error', message, basis: {field, sources, location}});
            track('vision:optimize', {sensors, outcome: 'error', added: 0, ms: Math.round(performance.now() - started)});
        };
        worker.onerror = e => fail(e.message || 'The optimizer stopped unexpectedly');
        worker.onmessage = (e: MessageEvent<OptimizerMessage>) => {
            const msg = e.data;
            if (msg.type === 'progress') {
                setProgress(msg.progress);
                return;
            }
            if (msg.type === 'error') {
                fail(msg.message);
                return;
            }
            stopWorker();
            const result = msg.result;
            let nextSources = sources;
            if (result.status === 'solved') {
                nextSources = [...sources, ...result.positions.map(({x, y}): Source => ({uid: uuidv4(), x, y, type: 'sensors', macroBins: false, sensors}))];
                setSources(nextSources);
            }
            setOutcome({...result, basis: {field, sources: nextSources, location}});
            track('vision:optimize', {
                sensors,
                outcome: result.status === 'uncoverable' ? 'uncoverable' : result.optimal ? 'optimal' : 'bound',
                added: result.status === 'solved' ? result.positions.length : 0,
                ms: Math.round(performance.now() - started),
            });
        };
        worker.postMessage({field, sources, location, sensors} satisfies OptimizeRequest);
    }, [field, sources, location, optimizeSensors, stopWorker]);

    const cancelOptimize = useCallback(() => {
        stopWorker();
        track('vision:optimize-cancel');
    }, [stopWorker]);

    const currentOutcome = outcome && outcome.basis.field === field && outcome.basis.sources === sources && outcome.basis.location === location ? outcome : null;

    const uncoverable = useMemo(() => {
        const size = gridMax(location) + 1;
        return new Set(currentOutcome?.status === 'uncoverable' ? currentOutcome.tiles.map(t => t.y * size + t.x) : []);
    }, [currentOutcome, location]);

    const toggleElement = useCallback((x: number, y: number, mode: TileMode, prevObstacle: number) => {
        if (mode === 'asteroid' || mode === 'blocking') {
            setField(prev => {
                let f = [...prev];
                f[y] = [...f[y]];
                if (prevObstacle !== OBSTACLE.NONE) {
                    f[y][x] = OBSTACLE.NONE;
                } else if (mode === 'asteroid') {
                    f[y][x] = OBSTACLE.ASTEROID
                } else if (mode === 'blocking') {
                    f[y][x] = OBSTACLE.BLOCKING;
                }
                return f;
            })
        }
        if (mode === 'source') {
            setSources(prev => [...prev, {uid: uuidv4(), x, y, type:'sensors', macroBins: false, sensors: 1}]);
        }
    }, []);

    const updateSource = useCallback((uid: string, field: keyof Source, value: string|number|boolean) => {
        setSources(prev => {
            let next = [...prev];
            let i = next.findIndex(s => s.uid === uid);
            next[i] = {...next[i]};
            // @ts-expect-error
            next[i][field] = value;
            return next;
        })
    }, []);

    const removeSource = useCallback((uid: string) => {
        setSources(p => p.filter(el => el.uid !== uid));
    }, []);

    return (
        <Page>
            <title>Vision Simulator</title>
            <Row>
                <Col grow={1}>
                    <h2>Vision Simulator</h2>
                </Col>
                <Col>
                    <SharedSave type="vision" content={currentData} savedData={savedData} itemPath="/vision"/>
                </Col>
                <Col>
                    <Button leftSection={<InfoIcon />} onClick={() => setShowInfo(true)}>Important Info</Button>
                </Col>
            </Row>
            <Paper shadow="xs" p="md" withBorder>
                <div className={style.wrapper}>
                    <div className={style.map}>
                        <div className={style.row}>
                            <CellHead/>
                            <CellHead label={0}/>
                            <CellHead label={1}/>
                            <CellHead label={2}/>
                            <CellHead label={3}/>
                            <CellHead label={4}/>
                            <CellHead label={5}/>
                            <CellHead label={6}/>
                            <CellHead label={7}/>
                            <CellHead label={8}/>
                            <CellHead label={9}/>
                            <CellHead label={10}/>
                            <CellHead label={11}/>
                            <CellHead label={12}/>
                            <CellHead label={13}/>
                            <CellHead label={14}/>
                            <CellHead label={15}/>
                            <CellHead label={16}/>
                            <CellHead label={17}/>
                            <CellHead label={18}/>
                            <CellHead label={19}/>
                            {location === 'ground' ? (<>
                                <CellHead label={20}/>
                                <CellHead label={21}/>
                            </>) : null}
                        </div>
                        {draw.map((list, i) => (
                            <div key={i} className={style.row}>
                                <CellHead label={i}/>
                                {list.map((el, j) => <FieldCell key={j} cell={el}
                                                                mode={mode}
                                                                uncoverable={uncoverable.has(i * list.length + j)}
                                                                toggleElement={toggleElement}/>)}
                            </div>)
                        )}
                    </div>
                    <div className={style.side}>
                        <h4 style={{marginTop: 0}}>Location</h4>
                        <div style={{display: 'flex', flexDirection: 'row', justifyContent: 'space-between'}}>
                            <SegmentedControl data={locations} onChange={val => {
                            setLocation(val);
                            track('vision:location-change', {location: val});
                        }} value={location} />
                            <AFImporter onParse={setField} />
                        </div>

                        <h4>Add Element</h4>
                        <SegmentedControl data={modes} onChange={(val: TileMode) => {
                            setMode(val);
                            track('vision:mode-change', {mode: val});
                        }} value={mode}/>
                        <hr/>
                        Total Obstacles: {countBlocking}<br/>
                        Total Asteroids: {countAsteroids}<br/>
                        Coverage: {coverage}/{totalTiles} ({Math.round(coverage / totalTiles * 1000) / 10}%)<br/>
                        <hr/>
                        <h4 className={style.optimizeTitle}>Optimize</h4>
                        <p className={style.optimizeHelp}>Adds the fewest sources needed to see every tile, keeping the existing ones.</p>
                        <Row align="end" gap={2}>
                            <Col>
                                <NumberInput label="Sensors" value={optimizeSensors} onChange={val => setOptimizeSensors(typeof val === 'number' ? val : 0)} style={{width: 90}} min={0} max={30} allowDecimal={false} disabled={optimizing}/>
                            </Col>
                            <Col>
                                <Button onClick={optimizeSources} loading={optimizing}>Optimize</Button>
                            </Col>
                            {optimizing ? <Col>
                                <Button variant="default" onClick={cancelOptimize}>Cancel</Button>
                            </Col> : null}
                        </Row>
                        {optimizing ? <div className={style.optimizeResult}>
                            {progress ? `Searching... best ${progress.best} sources, lower bound ${progress.lowerBound}` : 'Preparing...'}
                        </div> : null}
                        {!optimizing && currentOutcome ? <OptimizeOutcomeLine outcome={currentOutcome}/> : null}
                        <hr/>
                        {sources.map(s => <SourceRow key={s.uid} source={s} removeSource={removeSource} updateSource={updateSource}/>)}

                        {/*<h4>Export/Import</h4>*/}
                        {/*<InputTextarea value={impExp} onChange={e => setImpExp(e.target.value)}/><br/>*/}
                        {/*<div style={{display: 'flex', flexDirection: 'row', alignItems: 'center'}}>*/}
                        {/*    <Button onClick={() => setImpExp(JSON.stringify(field))} label="Export"/>*/}
                        {/*    <Button onClick={() => {*/}
                        {/*        let imp = JSON.parse(impExp);*/}
                        {/*        if (imp) {*/}
                        {/*            setField(imp);*/}
                        {/*        }*/}
                        {/*    }} label="Import" style={{margin: 6}}/>*/}
                        {/*    <Button onClick={() => setImpExp('')} label="Reset"/>*/}
                        {/*</div>*/}
                    </div>
                </div>
                <Modal style={{maxWidth: '50vw'}} opened={showInfo} onClose={() => setShowInfo(false)} title="Important Info">
                    <p className="text-red">WARNING: this tool has not been tested extensively. If you notice a discrepancy please report it on the discord server or in private with as many details as possible so it can be reproduced.</p>

                    <h4>How to add and remove elements</h4>
                    <p>
                    Select what you want to add (source, obstacles or asteroid) and then click on the map to place it.<br/>
                    Obstacles and asteroids will be toggled if clicked again, while multiple sources can be added at the same coordinates.<br />
                    </p>
                    <hr />
                    <h4>Managing sources</h4>
                    <p>
                        You can change the sensor power/perception used in the sidebar.<br/>
                        By default sources behave like Ships, Vehicles and Space Stations. Selecting "Use perception" will switch the source to behave like Character, NPCs, Droids and Creatures, using the number as the perception skill.<br />
                        If using as perception the "Use Macrobins" option will be available, adding 5 power to the source, as per the rules.
                    </p>
                    <hr />
                    <h4>Optimize</h4>
                    <p>
                        The optimizer adds the minimum number of sources (with the chosen sensors) needed to see every tile of the map, obstacles and asteroids included.<br/>
                        Existing sources are kept as they are, new sources can be placed on any tile except obstacles.<br/>
                        If the search takes too long the best placement found is used, together with a lower bound on the number of sources needed.
                        If some tiles can't be seen from any position they are highlighted on the map instead.
                    </p>
                    <hr />
                    <h4>Obstacles</h4>
                    <p>
                        Obstacles are blocking obstacles, like planets or facilities.<br />
                        Asteroids will instead behave like asteroid fields and decrease the sensor power.
                    </p>
                </Modal>
            </Paper>
        </Page>
    )

}


const CellHead = ({label}: {label?: string|number}) => {
    return <div className={style.cellHead}>{label}</div>;
}

const FieldCell = React.memo(function FieldCellRaw({cell, mode, uncoverable, toggleElement}: {
    cell: CellResult,
    mode: TileMode,
    uncoverable: boolean,
    toggleElement: (x: number, y: number, mode: TileMode, prevObstacle: number) => void,
}) {
    let s = style.cell;
    if (cell.obstacle === OBSTACLE.ASTEROID) {
        s += ' ' + style.asteroid;
    }
    if (cell.obstacle === OBSTACLE.BLOCKING) {
        s += ' ' + style.blocking;
    }
    if (mode !== 'read') {
        s += ' ' + style.clickable;
    }
    if (cell.sensors === 0) {
        s += ' ' + style.fog;
    }
    if (cell.hasSource) {
        s += ' ' + style.source;
    }
    if (uncoverable) {
        s += ' ' + style.uncoverable;
    }
    return (
        <div className={s} onClick={mode !== 'read' ? () => toggleElement(cell.x, cell.y, mode, cell.obstacle) : undefined}>{cell.sensors}</div>
    );
});


const OptimizeOutcomeLine = ({outcome}: {outcome: OptimizeResult | {status: 'error', message: string}}) => {
    if (outcome.status === 'error') {
        return <div className={style.optimizeResult + ' ' + style.optimizeError}>Optimization failed: {outcome.message}</div>;
    }
    if (outcome.status === 'uncoverable') {
        return (
            <div className={style.optimizeResult + ' ' + style.optimizeError}>
                {outcome.tiles.length} {outcome.tiles.length === 1 ? 'tile' : 'tiles'} can't be seen from any position, no sources added:{' '}
                {outcome.tiles.map(t => `(${t.x}, ${t.y})`).join(', ')}
            </div>
        );
    }
    const added = outcome.positions.length;
    if (added === 0) {
        return <div className={style.optimizeResult}>Every tile is already visible, no sources added</div>;
    }
    return (
        <div className={style.optimizeResult}>
            Added {added} {added === 1 ? 'source' : 'sources'}{outcome.optimal ? ' — proven optimal' : ` (lower bound ${outcome.lowerBound})`}
        </div>
    );
}

const SourceRow = ({source, updateSource, removeSource}: {source: Source, updateSource: (uid: string, field: keyof Source, value: string|number|boolean) => void, removeSource: (uid: string) => void}) => {

    return (
        <Row align="center">
            <Col grow={1}>({source.x}, {source.y})</Col>
            <Col>
                <Checkbox id={`source-type-${source.uid}`} label="Use perception" checked={source.type==='perception'} onChange={e => updateSource(source.uid, 'type', e.currentTarget.checked ? 'perception' : 'sensors')} />
                <Checkbox id={`source-macrobins-${source.uid}`} label="Use Macro Binoculars" checked={source.macroBins} onChange={e => updateSource(source.uid, 'macroBins', e.currentTarget.checked)} disabled={source.type !== 'perception'} />
            </Col>
            <Col><NumberInput value={source.sensors} onChange={val => updateSource(source.uid, 'sensors', val)} style={{width: 70}} min={0} max={source.type==='perception' ? 5 : 30}/></Col>
            <Col><ActionIcon variant="subtle" onClick={() => removeSource(source.uid)} title="Remove"><TrashIcon /></ActionIcon></Col>
        </Row>
    )
}


export default withRouteErrorBoundary(VisionTool);
